import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { WorkflowSummary } from '@/hooks/useWorkflows'

/** Provenance mode written by the fork endpoint. The two modes are the
 * same copy — they differ only in the `forked_from.mode` stamp and in the
 * dialog's wording (spec 2026-09-18 §4.2). */
export type WorkflowForkMode = 'take_ownership' | 'new_from_type'

export interface ForkWorkflowArgs {
  workflowId: string
  mode: WorkflowForkMode
  /** Optional display name; `null`/empty → the server's `<name> (copy)`. */
  name?: string | null
}

/** E1 ownership lanes: `POST /api/workflows/{id}/fork` copies a platform
 * `process`/`order` row into a new `source: custom` row published as v1.
 * The response is the new row's detail (a `WorkflowSummary` superset);
 * callers navigate to `/workflows/<id>`. Invalidates every `['workflows']`
 * query so the library, detail and version views pick up the new row. */
export function useWorkflowFork(): UseMutationResult<WorkflowSummary, Error, ForkWorkflowArgs> {
  const queryClient = useQueryClient()
  return useMutation<WorkflowSummary, Error, ForkWorkflowArgs>({
    mutationFn: ({ workflowId, mode, name }) =>
      apiClient.post<WorkflowSummary>(`/api/workflows/${workflowId}/fork`, {
        mode,
        name: name ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}
